import React, { useMemo, useState } from 'react';
import { X, FileDown } from 'lucide-react';

/** @param {object} receipt */
function getReceiptCreatedAt(receipt) {
  const raw = receipt?.createdAt ?? receipt?.created_at;
  if (raw == null) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** @param {'month' | 'quarter' | 'year'} period */
function receiptInPeriod(d, period) {
  if (!d) return false;
  const now = new Date();
  if (period === 'month') {
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }
  if (period === 'year') {
    return d.getFullYear() === now.getFullYear();
  }
  const y = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(y, q * 3, 1, 0, 0, 0, 0);
  const end = new Date(y, (q + 1) * 3, 0, 23, 59, 59, 999);
  return d >= start && d <= end;
}

function formatLocalDateTime(isoOrDate) {
  try {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** @param {'month' | 'quarter' | 'year'} period */
function getPeriodDescription(period) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const monthName = now.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  if (period === 'month') {
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    const fmt = (d) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return { title: `Current month (${monthName})`, range: `${fmt(start)} – ${fmt(end)}` };
  }

  if (period === 'year') {
    const start = new Date(y, 0, 1);
    const end = new Date(y, 11, 31);
    const fmt = (d) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    return { title: `Calendar year ${y}`, range: `${fmt(start)} – ${fmt(end)}` };
  }

  const q = Math.floor(m / 3) + 1;
  const start = new Date(y, Math.floor(m / 3) * 3, 1);
  const end = new Date(y, Math.floor(m / 3) * 3 + 3, 0);
  const fmt = (d) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return { title: `Q${q} ${y}`, range: `${fmt(start)} – ${fmt(end)}` };
}

function escapeCsvCell(val) {
  const s = String(val ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function row(...cells) {
  return cells.map(escapeCsvCell).join(',');
}

function receiptSubtotal(r) {
  return Math.round((parseFloat(r.subtotal) || 0) * 100) / 100;
}
function receiptTax(r) {
  return Math.round((parseFloat(r.tax) || 0) * 100) / 100;
}
function receiptTotal(r) {
  return Math.round((parseFloat(r.total) || 0) * 100) / 100;
}

function customerName(r) {
  return (r.customer_name || r.customerName || '').trim();
}
function customerPhone(r) {
  return (r.customer_phone || r.customerPhone || '').trim();
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object[]} props.receipts
 * @param {object | null} props.currentStore
 * @param {{ tax_rate?: number }} props.storeSettings
 */
export default function SalesTaxExportModal({
  open,
  onClose,
  receipts,
  currentStore,
  storeSettings,
}) {
  const [period, setPeriod] = useState(
    /** @type {'month' | 'quarter' | 'year'} */ ('month'),
  );

  const { filtered, sums, periodMeta } = useMemo(() => {
    const list = (receipts || []).filter((r) => {
      const d = getReceiptCreatedAt(r);
      return receiptInPeriod(d, period);
    });
    let sumSub = 0;
    let sumTax = 0;
    let sumTot = 0;
    for (const r of list) {
      sumSub += receiptSubtotal(r);
      sumTax += receiptTax(r);
      sumTot += receiptTotal(r);
    }
    sumSub = Math.round(sumSub * 100) / 100;
    sumTax = Math.round(sumTax * 100) / 100;
    sumTot = Math.round(sumTot * 100) / 100;
    return {
      filtered: list,
      sums: { count: list.length, sumSub, sumTax, sumTot },
      periodMeta: getPeriodDescription(period),
    };
  }, [receipts, period]);

  const taxRateLabel =
    storeSettings?.tax_rate !== undefined && storeSettings?.tax_rate !== null
      ? String(storeSettings.tax_rate)
      : '';

  const buildCsv = () => {
    const storeName = currentStore?.name || 'Store';
    const generated = new Date().toISOString();
    const lines = [];

    lines.push(row('Field', 'Value'));
    lines.push(row('Store', storeName));
    lines.push(row('Export period', periodMeta.title));
    lines.push(row('Date range (local)', periodMeta.range));
    lines.push(row('Generated (UTC)', generated));
    if (taxRateLabel !== '') lines.push(row('Store tax rate %', taxRateLabel));
    lines.push(row('Receipts in period', String(sums.count)));
    lines.push(row('Sum subtotal', sums.sumSub.toFixed(2)));
    lines.push(row('Sum tax', sums.sumTax.toFixed(2)));
    lines.push(row('Sum total', sums.sumTot.toFixed(2)));
    lines.push('');
    lines.push(
      row('Receipt ID', 'Created (local)', 'Customer', 'Phone', 'Subtotal', 'Tax', 'Total'),
    );
    for (const r of filtered) {
      const d = getReceiptCreatedAt(r);
      const created = d ? formatLocalDateTime(d) : '';
      lines.push(
        row(
          String(r.id ?? ''),
          created,
          customerName(r),
          customerPhone(r),
          receiptSubtotal(r).toFixed(2),
          receiptTax(r).toFixed(2),
          receiptTotal(r).toFixed(2),
        ),
      );
    }

    return '\uFEFF' + lines.join('\r\n');
  };

  const handleDownload = () => {
    const csv = buildCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeStore = (currentStore?.name || 'store')
      .replace(/[^\w\-]+/g, '-')
      .slice(0, 40);
    const day = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `sales-tax-${safeStore}-${period}-${day}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full sm:max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl border border-slate-200 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Sales &amp; tax export</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Download CSV for the selected period.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Period
            </span>
            <div className="mt-2 flex rounded-lg border border-slate-200 p-1 bg-slate-50 gap-1">
              {[
                { id: 'month', label: 'Month' },
                { id: 'quarter', label: 'Quarter' },
                { id: 'year', label: 'Year' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPeriod(id)}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors border ${
                    period === id
                      ? 'bg-white text-blue-600 shadow-sm border-slate-200'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm min-h-[5.5rem] flex flex-col justify-center">
            <p className="font-semibold text-slate-800 leading-snug">{periodMeta.title}</p>
            <p className="text-slate-600 mt-1 leading-snug">{periodMeta.range}</p>
          </div>

          <div className="rounded-xl border border-slate-200 p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Preview
            </p>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Receipts</span>
              <span className="font-semibold text-slate-900">{sums.count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-semibold text-slate-900">${sums.sumSub.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Tax</span>
              <span className="font-semibold text-slate-900">${sums.sumTax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Total</span>
              <span className="font-semibold text-slate-900">${sums.sumTot.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 p-4 border-t border-slate-200 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            data-tip="Saves a CSV of every receipt in this period with subtotal and tax"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}
